import type { RichTextReferences, RichTextViewProps } from '@app/rich-editor'
import { Fragment, type ReactNode } from 'react'
import { MediaPreview, type MediaPreviewAspect, type MediaPreviewFit } from './media-preview'

// Read-only renderer for `Content.data` (Slate JSON, formatVersion 2) — the node vocabulary of
// decision 21: paragraph, heading (h2–h4), unorderedList/orderedList/listItem, anchor, blockquote,
// calloutBox, image, youtube; marks isBold/isItalic. Injected into the editor through
// `RichTextViewProvider` for block previews; the web has its own string renderer over the same
// fixtures (`packages/lib/test/fixtures/slate`), so the two must stay in step.

type SlateText = { text: string; isBold?: boolean; isItalic?: boolean }
type SlateElement = { type: string; children: SlateNode[]; [attr: string]: unknown }
type SlateNode = SlateText | SlateElement

const isText = (node: SlateNode): node is SlateText => typeof (node as SlateText).text === 'string'
const asString = (v: unknown): string => (typeof v === 'string' ? v : '')
const asAspect = (v: unknown): MediaPreviewAspect => (v === '16/9' || v === '4/3' ? v : 'auto')
const asFit = (v: unknown): MediaPreviewFit => (v === 'contain' ? 'contain' : 'cover')

function renderLeaf(leaf: SlateText, key: number): ReactNode {
	let content: ReactNode = leaf.text.split('\n').map((part, i) => (
		<Fragment key={i}>
			{i > 0 && <br />}
			{part}
		</Fragment>
	))
	if (leaf.isBold) content = <strong>{content}</strong>
	if (leaf.isItalic) content = <em>{content}</em>
	return <Fragment key={key}>{content}</Fragment>
}

function renderNode(node: unknown, key: number, references: RichTextReferences): ReactNode {
	if (!node || typeof node !== 'object') return null
	const n = node as SlateNode
	if (isText(n)) return renderLeaf(n, key)
	// Element nodes come from free-form JSON and can lack `children` — render them empty, don't crash.
	const children = (Array.isArray(n.children) ? n.children : []).map((child, i) => renderNode(child, i, references))
	switch (n.type) {
		case 'paragraph':
			return <p key={key} className="my-4 leading-relaxed">{children}</p>
		case 'heading': {
			const level = Math.min(Math.max(typeof n.level === 'number' ? n.level : 2, 2), 4)
			if (level === 2) return <h2 key={key} className="mt-10 mb-4 text-2xl font-semibold leading-tight text-blue-primary">{children}</h2>
			if (level === 3) return <h3 key={key} className="mt-8 mb-3 text-xl font-semibold leading-snug text-blue-primary">{children}</h3>
			return <h4 key={key} className="mt-6 mb-2 text-lg font-semibold leading-snug text-blue-primary">{children}</h4>
		}
		case 'unorderedList':
			return <ul key={key} className="list-disc pl-5 my-4 space-y-1">{children}</ul>
		case 'orderedList':
			return <ol key={key} className="list-decimal pl-5 my-4 space-y-1">{children}</ol>
		case 'listItem':
			return <li key={key}>{children}</li>
		case 'anchor': {
			// The reference (when present and resolvable) is the truth about where the link goes; the
			// node's `href` is the authoring-time snapshot. Neither → plain text, not a dead `#`.
			const resolved = typeof n.referenceId === 'string' ? references[n.referenceId]?.href : undefined
			const href = resolved || asString(n.href)
			if (!href) return <Fragment key={key}>{children}</Fragment>
			return <a key={key} href={href} className="text-light-blue underline underline-offset-2">{children}</a>
		}
		case 'blockquote':
			return (
				<blockquote key={key} className="border-l-4 border-orange pl-5 my-6 italic text-gray [&>:first-child]:mt-0 [&>:last-child]:mb-0">
					{children}
				</blockquote>
			)
		case 'calloutBox':
			return <div key={key} className="rounded-xl bg-light-gray p-6 my-6 [&>:first-child]:mt-0 [&>:last-child]:mb-0">{children}</div>
		case 'image': {
			const image = typeof n.referenceId === 'string' ? references[n.referenceId] : undefined
			if (!image?.url) return null
			return (
				<MediaPreview key={key} src={image.url} alt={image.alt} caption={asString(n.caption) || null} aspect={asAspect(n.aspect)} fit={asFit(n.fit)} />
			)
		}
		case 'youtube': {
			const video = typeof n.referenceId === 'string' ? references[n.referenceId] : undefined
			if (!video?.youtubeId) return null
			return (
				<div key={key} className="my-6 aspect-video overflow-hidden rounded-xl bg-light-gray">
					<iframe
						src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(video.youtubeId)}`}
						title="YouTube video"
						loading="lazy"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
						allowFullScreen
						className="h-full w-full border-0"
					/>
				</div>
			)
		}
		default:
			// Unknown node: keep the text visible rather than swallowing content.
			return <Fragment key={key}>{children}</Fragment>
	}
}

export function RichTextView({ value, references = {}, className }: RichTextViewProps): ReactNode {
	const children = Array.isArray(value?.children) ? value.children : []
	return <div className={className}>{children.map((node, i) => renderNode(node, i, references))}</div>
}
